import React, { useState, useEffect } from 'react';
import { ChevronRight, Menu, X, Download, MapPin, Flame, Award, Star, Instagram, Facebook } from 'lucide-react';
import bbqHeroImage from "./brisket-background.jpeg";

const BBQAppLanding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-amber-50 text-gray-900 font-sans">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrollPosition > 50 ? 'bg-amber-900 shadow-lg py-2' : 'bg-transparent py-4'}`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center">
            <div className="text-2xl font-bold text-amber-500">
              <span className="text-red-600">BBQ</span>FINDER
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-white hover:text-amber-400 transition">Features</a>
            <a href="#discover" className="text-white hover:text-amber-400 transition">Discover</a>
            <a href="#download" className="text-white hover:text-amber-400 transition">Download</a>
            <a href="#about" className="text-white hover:text-amber-400 transition">About Us</a>
            <button className="bg-red-600 text-white px-5 py-2 rounded-full font-medium hover:bg-red-700 transition flex items-center">
              Get The App <ChevronRight className="ml-1 h-4 w-4" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-amber-900 absolute w-full">
            <div className="flex flex-col px-4 py-6 space-y-4">
              <a href="#features" className="text-white hover:text-amber-400 transition" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="#discover" className="text-white hover:text-amber-400 transition" onClick={() => setIsMenuOpen(false)}>Discover</a>
              <a href="#download" className="text-white hover:text-amber-400 transition" onClick={() => setIsMenuOpen(false)}>Download</a>
              <a href="#about" className="text-white hover:text-amber-400 transition" onClick={() => setIsMenuOpen(false)}>About Us</a>
              <button className="bg-red-600 text-white px-5 py-2 rounded-full font-medium hover:bg-red-700 transition flex items-center w-full justify-center">
                Get The App <ChevronRight className="ml-1 h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center">
        <div className="absolute inset-0 bg-amber-900 bg-opacity-10 z-[0]"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center z-[0]" 
          style={{ 
            //backgroundImage: `url('/api/placeholder/1920/1080')`,
            backgroundImage: `url(${ bbqHeroImage })`,
            backgroundPosition: 'center',
          }}
        ></div>
        <div className="container mx-auto px-4 z-10">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Discover Authentic American BBQ</h1>
            <p className="text-xl text-amber-100 mb-8">Explore legendary pitmasters, secret recipes, and the best BBQ joints across the United States</p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button className="bg-red-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-red-700 transition flex items-center justify-center">
                <Download className="mr-2 h-5 w-5" /> App Store
              </button>
              <button className="bg-amber-500 text-amber-900 px-8 py-4 rounded-lg font-medium hover:bg-amber-400 transition flex items-center justify-center">
                <Download className="mr-2 h-5 w-5" /> Google Play
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Your Ultimate BBQ Companion</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Everything you need to find, enjoy, and master authentic American barbecue</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<MapPin className="h-10 w-10 text-red-600" />}
              title="Find BBQ Spots"
              description="Discover the best local BBQ joints, from famous destinations to hidden gems across all 50 states"
            />
            <FeatureCard 
              icon={<Flame className="h-10 w-10 text-red-600" />}
              title="Authentic Recipes"
              description="Learn signature techniques and recipes from legendary pitmasters for every regional style"
            />
            <FeatureCard 
              icon={<Award className="h-10 w-10 text-red-600" />}
              title="BBQ Competitions"
              description="Stay updated on competitions, festivals, and BBQ events happening near you"
            />
          </div>
        </div>
      </section>

      {/* App Preview Section */}
      <section id="discover" className="py-20 bg-gradient-to-b from-amber-50 to-amber-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Explore BBQ Like Never Before</h2>
              <div className="space-y-6">
                <FeatureItem 
                  number="01"
                  title="Interactive BBQ Map"
                  description="Navigate through our curated map of the most authentic BBQ establishments across the country"
                />
                <FeatureItem 
                  number="02"
                  title="Pitmaster Profiles"
                  description="Learn from the masters and their unique approaches to the craft of barbecue"
                />
                <FeatureItem 
                  number="03"
                  title="Flavor Profiles"
                  description="Understand regional differences in rubs, sauces, woods, and cooking methods"
                />
                <FeatureItem 
                  number="04"
                  title="Community Reviews"
                  description="Real ratings and tips from fellow BBQ enthusiasts who know their smoke"
                />
              </div>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <div className="relative">
                <div className="bg-amber-900 w-64 h-96 rounded-3xl shadow-xl transform rotate-6 absolute top-4 right-4"></div>
                <div className="bg-red-600 w-64 h-96 rounded-3xl shadow-xl transform -rotate-6 absolute top-4 left-4"></div>
                <div className="bg-white w-72 h-96 rounded-3xl shadow-2xl relative z-10 overflow-hidden">
                  <div className="h-full w-full bg-cover bg-center" style={{ backgroundImage: `url('/api/placeholder/400/800')` }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What BBQ Lovers Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Join thousands of barbecue enthusiasts already using our app</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <TestimonialCard 
              quote="This app changed my BBQ game forever. Found techniques I never knew existed!"
              author="Mike T."
              location="Texas"
              rating={5}
            />
            <TestimonialCard 
              quote="I discovered the best hidden BBQ joint in my town that I'd driven past for years."
              author="Sarah L."
              location="North Carolina"
              rating={5}
            />
            <TestimonialCard 
              quote="The regional guides are fantastic. Finally understand the difference between Carolina and Memphis style."
              author="David R."
              location="Missouri"
              rating={4}
            />
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-20 bg-amber-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Get Started Today</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto">Download the app and begin your journey through America's rich BBQ heritage</p>
          
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button className="bg-black text-white px-8 py-4 rounded-lg font-medium hover:bg-gray-900 transition flex items-center justify-center">
              <Download className="mr-2 h-5 w-5" /> App Store
            </button>
            <button className="bg-white text-amber-900 px-8 py-4 rounded-lg font-medium hover:bg-gray-100 transition flex items-center justify-center">
              <Download className="mr-2 h-5 w-5" /> Google Play
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">About BBQ Finder</h2>
            <p className="text-lg mb-8">
              Created by a team of BBQ enthusiasts with a passion for preserving and celebrating authentic American barbecue traditions. Our mission is to connect BBQ lovers with the rich heritage, diverse regional styles, and incredible flavors that make American BBQ a national treasure.
            </p>
            <p className="text-lg mb-8">
              Whether you're a backyard griller, a competition pitmaster, or simply someone who appreciates good food, SmokeSignal is your guide to the smoky, delicious world of American BBQ.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold text-amber-500 mb-4">
                <span className="text-red-600">BBQ</span>FINDER
              </div>
              <p className="text-amber-200">Your ultimate guide to authentic American BBQ</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Navigation</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="text-amber-200 hover:text-white transition">Features</a></li>
                <li><a href="#discover" className="text-amber-200 hover:text-white transition">Discover</a></li>
                <li><a href="#download" className="text-amber-200 hover:text-white transition">Download</a></li>
                <li><a href="#about" className="text-amber-200 hover:text-white transition">About Us</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-amber-200 hover:text-white transition">Help Center</a></li>
                <li><a href="#" className="text-amber-200 hover:text-white transition">Contact Us</a></li>
                <li><a href="#" className="text-amber-200 hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="text-amber-200 hover:text-white transition">Terms of Service</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Connect</h3>
              <div className="flex space-x-4">
                <a href="#" className="text-amber-200 hover:text-white transition">
                  <Instagram className="h-6 w-6" />
                </a>
                <a href="#" className="text-amber-200 hover:text-white transition">
                  <Facebook className="h-6 w-6" />
                </a>
                <a href="#" className="text-amber-200 hover:text-white transition">
                  <X className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-amber-800 mt-8 pt-8 text-center text-amber-300">
            <p>&copy; {new Date().getFullYear()} BBQ Finder LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => {
  return (
    <div className="bg-amber-50 p-8 rounded-xl shadow-lg transition duration-300 hover:shadow-xl hover:transform hover:-translate-y-1">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

// Feature Item Component
const FeatureItem = ({ number, title, description }) => {
  return (
    <div className="flex">
      <div className="mr-4">
        <div className="bg-red-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">
          {number}
        </div>
      </div>
      <div>
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ quote, author, location, rating }) => {
  return (
    <div className="bg-amber-50 p-6 rounded-xl shadow-lg">
      <div className="flex mb-4">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            className={`h-5 w-5 ${i < rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} 
          />
        ))}
      </div>
      <p className="italic mb-4">"{quote}"</p>
      <div>
        <p className="font-bold">{author}</p>
        <p className="text-gray-600 text-sm">{location}</p>
      </div>
    </div>
  );
};

export default BBQAppLanding;